import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Clock, Truck } from "lucide-react";
import GoodsReceipt from "./GoodsReceipt";
import { TransferRow } from "./Transfers";

/** Receiver-only view: incoming transfers, GRN form, last-24h entries. */
export default function ReceiverHome() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["receiver-recent"],
    queryFn: async () => (await api.get("/dashboard")).data,
    refetchInterval: 30_000,
  });
  const { data: incoming = [] } = useQuery({
    queryKey: ["transfers-incoming"],
    queryFn: async () => (await api.get("/transfers", { params: { status: "in_transit" } })).data,
    refetchInterval: 30_000,
  });
  return (
    <div className="space-y-6" data-testid="receiver-home">
      <div>
        <div className="data-label">Hi, {user?.name}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Log a goods receipt</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Fill and submit. Your manager will approve before it hits stock.
        </p>
      </div>
      {incoming.length > 0 && (
        <div className="bg-white border border-secondary/50 rounded-sm" data-testid="receiver-transfers">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Truck className="w-4 h-4 text-secondary" />
            <h2 className="font-display text-lg font-semibold">
              Parcels on the way · {incoming.length}
            </h2>
          </div>
          <div className="divide-y divide-border">
            {incoming.map(t => <TransferRow key={t.id} t={t} receiverMode />)}
          </div>
        </div>
      )}
      <GoodsReceipt receiverMode />
      <div className="bg-white border border-border rounded-sm">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-display text-lg font-semibold">Your entries · last 24 h</h2>
        </div>
        {(!data?.recent_entries || data.recent_entries.length === 0) ? (
          <div className="p-6 text-sm text-muted-foreground">No entries yet.</div>
        ) : (
          <ul className="divide-y divide-border" data-testid="receiver-entries">
            {data.recent_entries.map((t) => (
              <li key={t.id} className="p-4 flex items-center gap-3 text-sm">
                <span className={`badge-flat ${t.status === "posted" ? "bg-success/10 text-success"
                  : t.status === "pending_approval" ? "bg-secondary/15 text-secondary"
                  : "bg-destructive/10 text-destructive"}`}>
                  {t.status.replace("_", " ")}
                </span>
                <div className="min-w-0 flex-1 truncate">
                  <span className="font-mono text-xs text-muted-foreground">
                    {new Date(t.timestamp).toLocaleString()}
                  </span>
                  <span className="ml-2">{t.packaging_note || "—"}</span>
                </div>
                <div className="font-mono text-sm">{t.qty_change > 0 ? "+" : ""}{t.qty_change}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
